import { Controller } from "@hotwired/stimulus"

// Hold a day column's height while the pointer is inside it.
//
// Removing a meal makes the card genuinely shorter: the row disappears,
// everything below slides up, and the card you are working in changes size
// mid-click. Pinning the height on entry means a run of removals is still,
// and the card settles once when you leave.
export default class extends Controller {
  // TOUCH IS NOT HOVER. iOS synthesises a hover on the first tap of an
  // element that reacts to one and withholds the click until the second —
  // so this ran on a phone, the card grew, and the tap that was meant to
  // open it was spent. pointerenter says which kind of pointer it was;
  // mouseenter cannot. Cody, 12 Sep.
  enter(event) {
    if (event?.pointerType === "touch") return

    // The grid item's height BEFORE anything expands, so the row it sits in
    // keeps measuring the day at rest.
    //
    // Expanding swaps a row of macro figures for a ring and three bars —
    // about 90px more card — and the grid row sizes to its tallest item. On
    // the longest day of the week that gain landed on the document and gave
    // the page a scrollbar for as long as you pointed at it. Captured here,
    // at mouseenter, because a moment later it is not the resting height any
    // more.
    this.element.style.setProperty(
      "--day-rest-height", `${this.element.offsetHeight}px`
    )

    // The card pin, in contrast, is measured AFTER the expansion: the card is
    // still one column wide at mouseenter, so pinning then captures a taller
    // layout than the widened card needs — and the pin would stretch it.
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.pin(), 460)
  }

  pin() {
    // Measure the CARD, not the wrapper this controller is attached to.
    //
    // The pin is applied as the card's min-height, so measuring the wrapper
    // fed it a number that included the card's own margin — a floor taller
    // than the card had ever been. Every hover grew the column a little,
    // the page got taller, and a scrollbar appeared on a grid that fits.
    const card = this.element.querySelector(".card")
    if (!card) return

    // Only ever grow the pin. A card that gains a meal while hovered should
    // keep the taller measurement; one that loses a meal should not shrink.
    const height = card.offsetHeight
    const pinned = parseFloat(this.element.style.getPropertyValue("--day-height")) || 0

    if (height > pinned) {
      this.element.style.setProperty("--day-height", `${height}px`)
    }
  }

  leave(event) {
    // Same reasoning as #enter: a synthetic touch hover leaving must not
    // undo state a real tap just set.
    if (event?.pointerType === "touch") return

    clearTimeout(this.timer)
    this.element.style.removeProperty("--day-height")

    // Released with the pointer, so the column goes back to sizing itself.
    // A day that gained a meal while hovered gets its real height back the
    // moment you leave, rather than being held at a stale measurement.
    this.element.style.removeProperty("--day-rest-height")
  }

  disconnect() {
    clearTimeout(this.timer)
  }
}
